import React, { FC, useEffect, useState } from 'react';

import {
  Flex,
  Modal,
  Button,
  Center,
  Spinner,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  InputLeftElement,
  FormControl,
  InputGroup,
  Input,
  InputRightElement,
  FormErrorMessage,
  Box,
  FormLabel,
} from '@chakra-ui/react';
import { MdCalendarToday } from 'react-icons/md';

import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import { Form, Formik, useFormikContext } from 'formik';

import { useSites } from 'hooks';

import { useMutation } from 'react-query';
import { initiateHarvestSummary } from 'api/inventory';

import getSortedData from 'utils/getSortedData';
import { convertUTCToLocal } from 'utils/dateUtils';

import FormikInput from 'components/primitives/FormikInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { InventoryReportTypeMap } from 'constants/itemtypes';

import {
  initialValues,
  validationSchema,
} from './AddHarvestSummaryModal.utils';

import {
  AddHarvestSummaryModalProps,
  AddHarvestSummaryModalViewProps,
} from './AddHarvestSummaryModal.props';

import { InventoryCount, InventoryReportTypeEnum } from 'types/inventory';
import { SiteFilterableSelect } from '../SiteFilterableSelect';
import ReactDatePicker from 'react-datepicker';
import { format } from 'date-fns';
import FormikMonthYearPicker from 'components/primitives/FormikMonthYearPicker';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
  noOfLines: 1,
};

const Months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const Years = [2020, 2021, 2022, 2023, 2024, 2025, 2026];

const AddHarvestSummaryModalView: FC<AddHarvestSummaryModalViewProps> = (
  props,
) => {
  const { isOpen, onClose, onSubmit, isLoading, selectedInventoryItem } = props;

  const { data: sites = [], isLoading: isLoadingSites } = useSites();

  const [startDate, setStartDate] = useState(new Date());

  return (
    <Modal
      size="3xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent p="10px 30px" borderRadius="15px">
        <ModalCloseButton _focus={{ outline: 'none' }} top="5" />
        <ModalHeader textAlign="center" fontWeight="600" color="#262626">
          Create Harvest Summary
        </ModalHeader>
        <ModalBody>
          <Formik
            onSubmit={(values) => {
              values.startDate = convertUTCToLocal(
                values.startDate,
                true,
                true,
                'yyyy-MM-DD',
              ) as string;
              values.endDate = convertUTCToLocal(
                values.endDate,
                true,
                true,
                'yyyy-MM-DD',
              ) as string;

              const monthYear = values.monthYear.split('/');
              const month = Number(monthYear[0]);
              const year = Number(monthYear[1]);

              onSubmit({
                site: values.site,
                name: values.name,
                month: month,
                year: year,
                reportType: Number(values.reportType),
                endingInventoryCount: values.endingInventoryCount,
                beginningInventoryCount: values.beginningInventoryCount,
                startDate: values.startDate,
                endDate: values.endDate,
              });
            }}
            initialValues={initialValues}
            validationSchema={validationSchema}>
            {({ isValid, setFieldValue }) => {
              return (
                <Form>
                  <Flex direction="column" gap="20px">
                    <Flex gap="20px">
                      <SiteFilterableSelect
                        label="Select Customer Site"
                        name="site"
                        isReadOnly={selectedInventoryItem ? true : false}
                        selectedInventoryItem={selectedInventoryItem ?? null}
                        labelProps={{ fontSize: '14px' }}
                        isRequired={true}
                      />

                      <FormikSelectInput
                        isRequired
                        placeholder="Select"
                        label="Report Type"
                        name="reportType"
                        isFromInventoryCount={
                          selectedInventoryItem ? true : false
                        }>
                        {Object.entries(InventoryReportTypeMap)?.map(
                          ([key, value]) => (
                            <option key={value} value={key}>
                              {value || ''}
                            </option>
                          ),
                        )}
                      </FormikSelectInput>
                    </Flex>

                    <DateFields
                      selectedInventoryItem={selectedInventoryItem ?? null}
                    />

                    <Flex gap="20px">
                      <FormikInput
                        name="name"
                        label="Report Name"
                        isRequired
                        labelProps={labelProps}
                      />

                      <FormikMonthYearPicker
                        labelProps={labelProps}
                        onChange={(month, year) => {
                          setFieldValue('monthYear', `${month}/${year}`);
                        }}
                        name="monthYear"
                      />

                      {/* <FormikSelectInput
                        isRequired
                        placeholder="Select"
                        label="Assigned Month"
                        name="month">
                        {Months.map((value) => (
                          <option key={value} value={value}>
                            {value || ''}
                          </option>
                        ))}
                      </FormikSelectInput>
                      <FormikSelectInput
                        isRequired
                        placeholder="Select"
                        label="Assigned Year"
                        name="year">
                        {Years.map((value) => (
                          <option key={value} value={value}>
                            {value || ''}
                          </option>
                        ))}
                      </FormikSelectInput> */}
                    </Flex>

                    <Flex my={8} flexDirection="row" justifyContent="center">
                      <Button
                        variant="outline"
                        marginRight="10px"
                        w="134px"
                        onClick={onClose}
                        isDisabled={isLoading}>
                        Cancel
                      </Button>
                      <Button
                        isDisabled={!isValid}
                        w="134px"
                        type="submit"
                        isLoading={isLoading}>
                        Create
                      </Button>
                    </Flex>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const DateFields: FC<AddHarvestSummaryModalProps> = (props) => {
  const selectedInventoryItem = props?.selectedInventoryItem;
  const { values, setFieldValue } = useFormikContext<any>();
  const [filteredData, setFilteredData] = useState<InventoryCount[]>([]);
  const site = values?.site as string;
  const reportType =
    values?.reportType === ''
      ? null
      : (Number(values?.reportType) as InventoryReportTypeEnum);
  const mtnFn = async () => {
    if (typeof reportType === 'number' && !!site) {
      return initiateHarvestSummary(site, reportType);
    }
  };

  const { data, mutate, isLoading } = useMutation(mtnFn, {
    onSuccess: (response) => {
      if (response) {
        let filtered = response.list.filter((r) => !r.isArchived);
        setFilteredData(filtered);
        if (
          reportType === InventoryReportTypeEnum.BEGINNING &&
          response?.beginning
        ) {
          setFieldValue(
            'startDate',
            convertUTCToLocal(response.beginning, true, true),
          );

          setFieldValue('beginningInventoryCount', null);
        }

        if (reportType === InventoryReportTypeEnum.ENDING && response?.ending) {
          setFieldValue(
            'endDate',
            convertUTCToLocal(response.ending, true, true),
          );
          setFieldValue('endingInventoryCount', null);
        }
      }
    },
  });
  useEffect(() => {
    mutate();
    setFieldValue('startDate', '');
    setFieldValue('endDate', '');
    setFieldValue('beginningInventoryCount', null);
    setFieldValue('endingInventoryCount', null);
  }, [reportType, site]);

  const handleInventoryCountChange = (selectedValue: any, field: string) => {
    const findInventory = filteredData?.find(
      (item) => item._id === selectedValue,
    );
    if (findInventory) {
      setFieldValue(field, findInventory.countDate);
    }
  };

  const renderInputs = () => {
    switch (reportType) {
      case InventoryReportTypeEnum.BEGINNING: {
        const options = filteredData.map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];

        return (
          <Flex gap="20px" align="flex-start">
            <FormikInput
              name="startDate"
              label="Beginning Inventory Count"
              isRequired
              isReadOnly
              labelProps={labelProps}
              leftElement={
                <InputLeftElement>
                  <MdCalendarToday color="primary.400" />
                </InputLeftElement>
              }
            />

            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Ending Inventory Count"
              name="endingInventoryCount"
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'endDate')
              }>
              {options.map((countDate, index) => (
                <option key={countDate} value={filteredData?.[index]._id}>
                  {countDate}
                </option>
              ))}
            </FormikSelectInput>
          </Flex>
        );
      }
      case InventoryReportTypeEnum.ENDING: {
        const options = (filteredData || []).map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];

        return (
          <Flex gap="20px" align="flex-start">
            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Beginning Inventory Count"
              name="beginningInventoryCount"
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'startDate')
              }>
              {options.map((countDate, index) => (
                <option key={countDate} value={filteredData?.[index]._id}>
                  {countDate}
                </option>
              ))}
            </FormikSelectInput>

            <FormikInput
              name="endDate"
              label="Ending Inventory Count"
              isRequired
              isReadOnly
              labelProps={labelProps}
              leftElement={
                <InputLeftElement>
                  <MdCalendarToday color="primary.400" />
                </InputLeftElement>
              }
            />
          </Flex>
        );
      }
      case InventoryReportTypeEnum.MONTHLY: {
        const beginningOptions = (filteredData || []).map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];

        const endingOptions = (filteredData || []).map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];
        return (
          <Flex gap="20px" align="flex-start">
            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Beginning Inventory Count"
              name="beginningInventoryCount"
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'startDate')
              }>
              {beginningOptions.map((beginningDate, index) => (
                <option key={beginningDate} value={filteredData?.[index]._id}>
                  {beginningDate}
                </option>
              ))}
            </FormikSelectInput>

            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Ending Inventory Count"
              name="endingInventoryCount"
              selectedInventoryItem={selectedInventoryItem ?? null}
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'endDate')
              }>
              {endingOptions.map((countDate, index) => (
                <option key={countDate} value={filteredData?.[index]._id}>
                  {countDate}
                </option>
              ))}
            </FormikSelectInput>
          </Flex>
        );
      }
      case InventoryReportTypeEnum.NO_INVENTORY: {
        return (
          <Flex gap="20px" align="flex-start">
            <FormikDatepicker
              isRequired
              name="startDate"
              label="Beginning Inventory Count"
              labelProps={labelProps}
            />
            <FormikDatepicker
              isRequired
              name="endDate"
              label="Ending Inventory Count"
              labelProps={labelProps}
            />
          </Flex>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {isLoading ? (
        <Center flex={1} py="20px">
          <Spinner />
        </Center>
      ) : (
        renderInputs()
      )}
    </>
  );
};

export default AddHarvestSummaryModalView;
